import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { getActiveLanguage, type SupportedLanguage } from '../i18n';
import { currentUserQuery, useUpdateLanguageMutation } from './auth';

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
    'de-CH': 'Deutsch (CH)',
    'fr-CH': 'Français (CH)',
    'it-CH': 'Italiano (CH)',
    'en-CH': 'English (CH)',
};

export function languageCode(language: string): string {
    return language.split('-')[0].toUpperCase();
}

export type LanguageSelection = {
    activeLanguage: string;
    select: (language: SupportedLanguage) => Promise<void>;
};

export function useLanguageSelection(): LanguageSelection {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useRouterState({ select: (s) => s.location });
    const queryClient = useQueryClient();
    const updateLanguage = useUpdateLanguageMutation();

    return {
        activeLanguage: getActiveLanguage(),
        select: async (language: SupportedLanguage): Promise<void> => {
            await i18n.changeLanguage(language);
            // Re-emit the current internal location so the output rewrite re-runs with the new locale.
            await navigate({
                to: location.pathname,
                search: location.search,
                hash: location.hash,
                replace: true,
            });
            if (queryClient.getQueryData(currentUserQuery.queryKey) !== undefined) {
                updateLanguage.mutate(language);
            }
        },
    };
}
